import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileSpreadsheet, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import PoPShell from '../PoPShell';
import { usePoPAuth } from '../../../context/PoPAuthContext';
import { capAdminOverview, capMeta, capDownload } from '../../../lib/capstoneApi';
import { StatusBadge, RagDot, inputCls, btnGhost, fmtDate } from '../../../components/capstone/shared';

const STATUSES = ['Draft', 'In Progress', 'Under Review', 'Completed', 'Archived'];

export default function CapstoneAdmin() {
  const { user, loading } = usePoPAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [domains, setDomains] = useState([]);
  const [domain, setDomain] = useState('');
  const [status, setStatus] = useState('');
  const [loadError, setLoadError] = useState('');
  const requestNumber = useRef(0);
  const loadOverview = useCallback(async () => {
    if (!user || !['super_admin', 'university'].includes(user.role)) return;
    const requestId = ++requestNumber.current;
    setData(null); setLoadError('');
    try {
      const result = await capAdminOverview({ ...(domain && { domain }), ...(status && { status }) });
      if (!Array.isArray(result.projects) || !result.stats) throw new Error('Invalid program response');
      if (requestNumber.current === requestId) setData(result);
    } catch (error) {
      if (requestNumber.current === requestId) setLoadError('The program overview could not be loaded. Please try again.');
    }
  }, [user, domain, status]);

  useEffect(() => { capMeta().then((m) => setDomains(m.domains)).catch(() => {}); }, []);
  useEffect(() => {
    if (loading || !user) return;
    if (!['super_admin', 'university'].includes(user.role)) { navigate('/pop/capstone', { replace: true }); return; }
    loadOverview();
    return () => { requestNumber.current += 1; };
  }, [user, loading, loadOverview, navigate]);

  if (!loading && !user) return <PoPShell activeTab="capstone"><div className="py-20 text-center">
    <p data-testid="cap-admin-signin-message" className="text-sm text-white/70">Sign in to view your authorized program projects.</p>
    <Link data-testid="cap-admin-signin-link" to="/pop/capstone" className={`${btnGhost} mt-4`}>Open Capstone Studio</Link>
  </div></PoPShell>;

  if (loadError) return <PoPShell activeTab="capstone"><div role="alert" data-testid="cap-admin-load-error" className="border border-amber-300/30 rounded-lg p-8 text-center">
    <p data-testid="cap-admin-load-error-message" className="text-sm text-white/80">{loadError}</p>
    <button data-testid="cap-admin-load-retry" onClick={loadOverview} className={`${btnGhost} mt-4`}>Retry loading program</button>
    <Link data-testid="cap-admin-error-back" to="/pop/capstone" className={`${btnGhost} mt-4 ml-2`}>Capstone Studio</Link>
  </div></PoPShell>;

  if (loading || !user || !data) {
    return (
      <PoPShell activeTab="capstone">
        <div data-testid="cap-admin-loading" role="status" className="flex items-center justify-center py-24 text-white/50"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading program view…</div>
      </PoPShell>
    );
  }

  const s = data.stats || {};

  return (
    <PoPShell activeTab="capstone">
      <button data-testid="cap-admin-back" onClick={() => navigate('/pop/capstone')} className="inline-flex items-center gap-1.5 text-[12px] text-white/55 hover:text-white mb-5">
        <ArrowLeft className="w-3.5 h-3.5" /> Capstone Studio
      </button>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-7">
        <div>
          <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">PROGRAM COORDINATOR VIEW</div>
          <h1 className="font-serif text-3xl">Cohort-wide capstone tracking.</h1>
        </div>
        <button className={btnGhost} data-testid="cap-cohort-export-btn"
          onClick={() => capDownload('/capstone/admin/export.xlsx', 'Capstone_cohort.xlsx').then(() => toast.success('Cohort summary downloaded')).catch(() => toast.error('Export failed'))}>
          <FileSpreadsheet className="w-4 h-4" /> Export cohort summary
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-7" data-testid="cap-admin-stats">
        {[[s.total ?? 0, 'Projects'], [`${s.avg_progress ?? 0}%`, 'Avg progress'], [s.at_risk ?? 0, 'At risk'], [s.completed ?? 0, 'Completed']].map(([v, l]) => (
          <div key={l} className="rounded-xl border border-white/10 bg-slate-900/50 p-4 text-center">
            <div className="font-serif text-2xl text-amber-300">{v}</div>
            <div className="text-[10px] tracking-[0.15em] uppercase text-white/45 mt-1">{l}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2.5 mb-5">
        <select className={`${inputCls} !w-auto`} value={domain} onChange={(e) => setDomain(e.target.value)} data-testid="cap-admin-domain-filter">
          <option value="">All domains</option>
          {domains.map((d) => <option key={d}>{d}</option>)}
        </select>
        <select className={`${inputCls} !w-auto`} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="cap-admin-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map((st) => <option key={st}>{st}</option>)}
        </select>
      </div>

      <div className="rounded-2xl border border-white/10 overflow-x-auto" data-testid="cap-admin-table">
        <table className="w-full text-left text-[12.5px]">
          <thead>
            <tr className="text-[10px] tracking-[0.15em] uppercase text-white/45 border-b border-white/10">
              {['Project', 'Domain', 'Lead', 'Mentors', 'Status', 'Progress', 'At risk', 'Target end', 'AI-assist'].map((h) => <th key={h} className="px-4 py-3 font-bold whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.projects.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-white/35 italic">No capstone projects match these filters.</td></tr>
            )}
            {data.projects.map((p) => (
              <tr key={p.id} className="border-b border-white/6 last:border-0 hover:bg-white/[0.03]">
                <td className="px-4 py-3">
                  <Link data-testid={`cap-admin-project-${p.id}`} to={`/pop/capstone/${p.id}`} className="text-white/85 hover:text-amber-200 font-medium">{p.title}</Link>
                  <div className="text-[10px] text-white/35">{p.team_name || `${(p.members || []).length} member(s)`}</div>
                </td>
                <td className="px-4 py-3 text-white/55 whitespace-nowrap">{p.domain}</td>
                <td className="px-4 py-3 text-white/55">{p.created_by_email}</td>
                <td className="px-4 py-3 text-white/55">{(p.mentors || []).map((m) => m.name || m.email).join(', ') || '—'}</td>
                <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-amber-300 rounded-full" style={{ width: `${p.progress_pct}%` }} /></div>
                    <span className="text-white/60">{p.progress_pct}%</span>
                  </div>
                  <RagDot rag={p.rag} />
                </td>
                <td className="px-4 py-3">
                  {(p.at_risk_milestones || []).length > 0 ? (
                    <span className="inline-flex items-center gap-1 text-amber-300 font-semibold"><ShieldAlert className="w-3.5 h-3.5" /> {p.at_risk_milestones.length}</span>
                  ) : <span className="text-white/30">—</span>}
                </td>
                <td className="px-4 py-3 text-white/55 whitespace-nowrap">{fmtDate(p.target_end_date)}</td>
                <td className="px-4 py-3 text-white/55">{p.ai_assist_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PoPShell>
  );
}
